import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import Card from "../../../components/Card";
import {
  addReferralDiscount,
  getDiscount,
  newgetAllReferrals,
  updateReferralDiscount,
} from "../../../services/service";
import { toast } from "react-toastify";

const NewReferralManage = () => {
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJoinedUsers, setSelectedJoinedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedReferralId, setSelectedReferralId] = useState(null);
  const [joinedUserModal, setJoinedUserModal] = useState(false);
  const [discountChecking, setDiscountChecking] = useState(true);
  const [discountId, setDiscountId] = useState(null);
  const [form, setForm] = useState({
    discountType: "",
    discountValue: "",
    expiryType: "never",
    expiryDays: "",
  });
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const limit = 10;

  // Fetch referrals
  const fetchReferrals = async (page = 1, search = "") => {
    setLoading(true);
    setError("");
    try {
      const res = await newgetAllReferrals({
        page,
        limit,
        search,
      });

      setReferrals(res?.data || []);
      setCurrentPage(res?.page || 1);
      setTotalPages(res?.totalPages || 1);
      setTotalReferrals(res?.totalReferrals || 0);
    } catch (err) {
      setError("No referral data found");
    } finally {
      setLoading(false);
    }
  };
  const fetchDiscount = async () => {
    setDiscountChecking(true);
    try {
      const res = await getDiscount();
      if (Array.isArray(res) && res.length > 0) {
        const existing = res[0];
        setForm({
          discountType: existing.discountType,
          discountValue: existing.discountValue,
          expiryType: existing.expiryType,
          expiryDays: existing.expiryDays || "",
        });
        setDiscountId(existing._id);
        setIsUpdateMode(true);
      } else {
        setForm({
          discountType: "",
          discountValue: "",
          expiryType: "never",
          expiryDays: "",
        });
        setDiscountId(null);
        setIsUpdateMode(false);
      }
    } catch (err) {
      setForm({
        discountType: "",
        discountValue: "",
        expiryType: "never",
        expiryDays: "",
      });
      setDiscountId(null);
      setIsUpdateMode(false);
    } finally {
      setDiscountChecking(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
    fetchDiscount();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReferrals(1, searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (isUpdateMode && discountId) {
        await updateReferralDiscount(
          discountId,
          form.discountType,
          Number(form.discountValue),
          form.expiryType,
          form.expiryType === "expire" ? Number(form.expiryDays) : undefined
        );
        toast.success("Referral Updated successfully");
      } else {
        const res = await addReferralDiscount(
          form.discountType,
          Number(form.discountValue),
          form.expiryType,
          form.expiryType === "expire" ? Number(form.expiryDays) : undefined
        );
        toast.success("Referral Created successfully");
        if (res?.data?._id) {
          setDiscountId(res.data._id);
          setIsUpdateMode(true);
        }
      }
      await fetchReferrals();
      await fetchDiscount();
      setShowModal(false);
    } catch (err) {
      setError("Failed to save referral discount");
    } finally {
      setLoading(false);
    }
  };
  const paginate = (page) => {
    fetchReferrals(page, searchTerm);
  };
  return (
    <Card className="mb-4">
      {/* Header */}
      <Card.Header>
        <Card.Header.Title>Referral Manager</Card.Header.Title>
        <div className="mt-2 mt-md-0 d-flex flex-wrap gap-2">
          {discountChecking ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <Button variant="light" size="sm" disabled>
                {form.discountType === "percentage"
                  ? `Discount: ${form.discountValue}%`
                  : form.discountType === "amount"
                    ? `Discount: Rs. ${form.discountValue}`
                    : "No Discount"}
              </Button>
              <Button variant="light" size="sm" disabled>
                {form.expiryType === "expire"
                  ? `Expire: ${form.expiryDays} Days`
                  : "Expire: Never"}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowModal(true)}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : isUpdateMode ? (
                  "Update Discount"
                ) : (
                  "Add Discount"
                )}
              </Button>
            </>
          )}
        </div>
      </Card.Header>

      <Card.Body>
        {/* Search */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by Referral Code and UserName"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>

        {/* Total Count */}
        <div className="mb-2">
          <Button variant="light" size="sm" disabled>
            Total Referrals: {totalReferrals}
          </Button>
        </div>

        {/* Error */}
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Table */}
        <div className="table-responsive">
          <Table striped hover className="mb-0">
            <thead>
              <tr>
                <th>User Name</th>
                <th>Referral Code</th>
                <th>Joined Users</th>
                <th>Wallet Balance    </th>
                {/* <th>Actions</th> */}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : referrals.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No Data Available
                  </td>
                </tr>
              ) : (
                referrals.map((ref) => (
                  <tr key={ref._id}>
                    <td>{ref.generatedBy?.name}</td>
                    <td>{ref.referralCode}</td>

                    <td>
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => {
                          setSelectedJoinedUsers(ref.joinedUsers || []);
                          setJoinedUserModal(true);
                        }}
                      >
                        {ref.joinedUsers?.length}
                      </Button>
                    </td>
                    <td>{ref?.generatedBy?.walletBalance}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination className="mt-3 justify-content-center flex-wrap">
            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => paginate(currentPage - 1)}
            />

            {/* Show first page if not in window */}
            {currentPage > 3 && (
              <>
                <Pagination.Item onClick={() => paginate(1)}>1</Pagination.Item>
                {currentPage > 4 && <Pagination.Ellipsis disabled />}
              </>
            )}

            {/* Window of pages around current page */}
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (page >= currentPage - 2 && page <= currentPage + 2) {
                return (
                  <Pagination.Item
                    key={page}
                    active={currentPage === page}
                    onClick={() => paginate(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              }
              return null;
            })}

            {/* Show last page if not in window */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && <Pagination.Ellipsis disabled />}
                <Pagination.Item onClick={() => paginate(totalPages)}>
                  {totalPages}
                </Pagination.Item>
              </>
            )}

            <Pagination.Next
              disabled={currentPage === totalPages}
              onClick={() => paginate(currentPage + 1)}
            />
          </Pagination>
        )}
      </Card.Body>

      <Modal
        show={joinedUserModal}
        onHide={() => setJoinedUserModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Joined Users</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedJoinedUsers.length === 0 ? (
            <p>No users joined with this referral.</p>
          ) : (
            <ul>
              {selectedJoinedUsers.map((u, i) => (
                <li key={i}>
                  {u?.name || "Unknown User"} – {u?.email || ""}
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setJoinedUserModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* modal ---------------- */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isUpdateMode
              ? "Update Referral Discount"
              : "Add Referral Discount"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            {/* Discount Type */}
            <Form.Group className="mb-3">
              <Form.Label>Discount Type</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {/* <Form.Check
                  inline
                  type="radio"
                  label="Percentage"
                  name="discountType"
                  value="percent"
                  checked={form.discountType === "percent"}
                  onChange={(e) =>
                    setForm({ ...form, discountType: e.target.value })
                  }
                /> */}
                <Form.Check
                  inline
                  type="radio"
                  label="Amount"
                  name="discountType"
                  value="amount"
                  checked={form.discountType === "amount"}
                  onChange={(e) =>
                    setForm({ ...form, discountType: e.target.value })
                  }
                />
              </div>
            </Form.Group>

            {/* Discount Value */}
            <Form.Group className="mb-3">
              <Form.Label>Discount Value</Form.Label>
              <Form.Control
                type="number"
                value={form.discountValue}
                onChange={(e) =>
                  setForm({ ...form, discountValue: e.target.value })
                }
                min="0"
                required
              />
            </Form.Group>

            {/* Expiry Type */}
            {/* <Form.Group className="mb-3">
              <Form.Label>Expiry Type</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                <Form.Check
                  inline
                  type="radio"
                  label="Never Expire"
                  name="expiryType"
                  value="never"
                  checked={form.expiryType === "never"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expiryType: e.target.value,
                      expiryDays: "",
                    })
                  }
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Expire After Days"
                  name="expiryType"
                  value="days"
                  checked={form.expiryType === "days"}
                  onChange={(e) =>
                    setForm({ ...form, expiryType: e.target.value })
                  }
                />
              </div>
            </Form.Group> */}

            {/* Expiry Days */}
            {/* {form.expiryType === "days" && (
              <Form.Group className="mb-3">
                <Form.Label>Expiry Days</Form.Label>
                <Form.Control
                  type="number"
                  value={form.expiryDays}
                  onChange={(e) =>
                    setForm({ ...form, expiryDays: e.target.value })
                  }
                  min="1"
                  required
                />
              </Form.Group>
            )} */}

            <div className="d-flex justify-content-end gap-2 flex-wrap">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : isUpdateMode ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default NewReferralManage;
